///
/// EarthBrowser
///
/// Created by Alex Giger on 7/3/2024
/// Copyright (c) 2024 Geodata Labs
///
/// Aurora scene node. The H3 hex mesh (HexGridGeometry) carries the OVATION
/// probability for the current and next frame; whenever that pair changes
/// it is baked into a probability field texture (AuroraFieldBaker), and
/// every frame a shell at the top of the auroral layer is drawn with the
/// ray-marching volume shader (aurora_volume.glsl) that reads the field.
///

import { type DatasetSeries } from "@/earthbrowser/datasets/DatasetSeries"
import { AuroraFieldBaker } from "@/earthbrowser/dataset-distribution/aurora/AuroraFieldBaker"
import { AuroraShellGeometry } from "@/earthbrowser/dataset-distribution/aurora/AuroraShellGeometry"
import { type HexGridDataFrame } from "@/earthbrowser/dataset-distribution/aurora/HexGridDataFrame"
import { HexGridGeometry } from "@/earthbrowser/dataset-distribution/aurora/HexGridGeometry"
import { isMemoryConstrainedDevice } from "@/earthbrowser/utils/MemoryLog"
import { wgs } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet, Renderer } from "@stovokor/engine/Renderer"
import { RenderState } from "@stovokor/engine/RenderState"
import { type matrix } from "@stovokor/math/matrix"
import { type vec3 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { BlendFactor } from "@stovokor/shader/GLConstants"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type EarthBrowserContext } from "../../EarthBrowserContext"

// Top of the marched layer (km); must match cTopKm in aurora_volume.glsl
const cTopKm = 300

export class HexGridMaterial implements Material {
	public transparency = true
	public available: boolean = true
	// Emission per km of path at full probability; tuned so a nadir view of
	// a 90% forecast reads as a bright, not clipped, curtain
	public gain = 0.012
}

export class HexGridDataFeature extends NodeBase {
	private context: EarthBrowserContext
	public hexGridGeometry: HexGridGeometry
	public material: HexGridMaterial
	public volumeRenderer: AuroraVolumeRenderer
	public fieldTexture: Texture

	private baker: AuroraFieldBaker
	private shellGeometry: AuroraShellGeometry
	private dataSeries: DatasetSeries<HexGridDataFrame>
	// Frames whose vertex data currently sit in the two GPU value streams.
	// They are the same frame while the next one hasn't loaded yet
	private uploadedCurrent?: HexGridDataFrame
	private uploadedNext?: HexGridDataFrame
	// The field texture lags the value streams until the bake pass runs
	private fieldDirty = false

	constructor(context: EarthBrowserContext, gridResolution: number, dataSeries: DatasetSeries<HexGridDataFrame>) {
		super()
		this.context = context
		this.dataSeries = dataSeries
		this.material = new HexGridMaterial()
		this.hexGridGeometry = new HexGridGeometry(context, gridResolution)

		// Field resolution and march depth scale with the device: 1024 px
		// per hemisphere is ~24 km per texel at auroral latitudes (RG8 +
		// mips ~5.6 MB); constrained devices take half that in each axis
		const constrained = isMemoryConstrainedDevice()
		const hemispherePx = constrained ? 512 : 1024
		this.baker = new AuroraFieldBaker(context)
		this.fieldTexture = this.baker.createTarget(hemispherePx)

		this.shellGeometry = new AuroraShellGeometry(context, wgs.a + cTopKm)
		this.volumeRenderer = new AuroraVolumeRenderer(context, this.shellGeometry, [this.material], this.fieldTexture, {
			shellRadius: wgs.a + cTopKm,
			maxSteps: constrained ? 20 : 48,
			coverageLod: Math.log2(hemispherePx / 64),
		})
		this.renderer = this.volumeRenderer
	}

	override renderActions(state: RenderState): RenderActionSet[] {
		const clock = this.context.clock.clockTime
		this.dataSeries.update(clock)
		this.syncFrames()
		if (this.fieldDirty) {
			this.fieldDirty = !this.baker.bake(this.hexGridGeometry, this.fieldTexture)
		}
		if (!this.fieldTexture.available) {
			return []
		}

		// Crossfade position between the two uploaded frames by scene time
		let blend = 0
		if (this.uploadedCurrent && this.uploadedNext && this.uploadedNext !== this.uploadedCurrent) {
			const start = this.uploadedCurrent.range.start.julian
			const end = this.uploadedNext.range.start.julian
			if (end > start) {
				blend = Math.min(Math.max((clock.julian - start) / (end - start), 0), 1)
			}
		}
		this.volumeRenderer.blend.value = blend
		this.volumeRenderer.opacity.value = this.dataSeries.availabilityFactor
		this.volumeRenderer.gain.value = this.material.gain

		return super.renderActions(state)
	}

	/**
	 * Keep the GPU value streams holding the series' current frame and the
	 * frame after it. Uploads only on change, rebuilds the active-cell index
	 * set and marks the field for re-baking whenever either stream changes.
	 * While the next frame is still loading, the current frame fills both
	 * streams (blend is a no-op).
	 */
	private syncFrames(): void {
		const current = this.dataSeries.currentFrame
		if (!current?.vertexData) return

		const next = this.dataSeries.nextFrame
		const nextSource = next?.vertexData ? next : current
		const currentChanged = current !== this.uploadedCurrent
		const nextChanged = nextSource !== this.uploadedNext
		if (!currentChanged && !nextChanged) return

		if (currentChanged) {
			this.hexGridGeometry.valueBuffer.uploadData(current.vertexData)
			this.uploadedCurrent = current
		}
		if (nextChanged) {
			this.hexGridGeometry.nextValueBuffer.uploadData(nextSource.vertexData!)
			this.uploadedNext = nextSource
		}
		this.hexGridGeometry.setActiveCells(current.vertexData, nextSource.vertexData!)
		this.fieldDirty = true
	}

	override destroy(): void {
		// The renderer destroys the shell geometry; the field mesh and the
		// bake resources are ours
		super.destroy()
		this.baker.destroy()
		this.fieldTexture.destroy()
		this.hexGridGeometry.destroy()
	}
}

interface VolumeSettings {
	shellRadius: number
	maxSteps: number
	coverageLod: number
}

class AuroraVolumeRenderer extends Renderer {
	private modelViewProjection = new UniformDynamic<matrix>()
	private cameraPosition = new UniformDynamic<vec3>()
	private lightDirection = new UniformDynamic<vec3>()
	private time = new UniformDynamic<number>()
	private shellRadius = new UniformDynamic<number>()
	private maxSteps = new UniformDynamic<number>()
	private coverageLod = new UniformDynamic<number>()
	private field = new UniformDynamic<Texture>()
	public blend = new UniformDynamic<number>()
	public opacity = new UniformDynamic<number>()
	public gain = new UniformDynamic<number>()
	private startJulian?: number

	constructor(context: StovokorContext, model: Geometry, materials: Material[], field: Texture, settings: VolumeSettings) {
		super(context, new ShaderSourceDefinition("aurora_volume"), model, materials)
		// Both faces (the shell is seen from outside in orbit and from inside
		// on the ground), no depth test (planet occlusion is analytic in the
		// shader), additive blend (the aurora emits light over whatever is
		// behind it)
		this.drawState.cullFace = false
		this.drawState.depthTest = false
		this.drawState.depthWrite = false
		this.drawState.blend = [BlendFactor.SrcAlpha, BlendFactor.One]

		this.field.value = field
		this.shellRadius.value = settings.shellRadius
		this.maxSteps.value = settings.maxSteps
		this.coverageLod.value = settings.coverageLod
		this.blend.value = 0
		this.opacity.value = 1
		this.gain.value = 0.012
		this.time.value = 0
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		this.cameraPosition.value = state.camera.position
		this.lightDirection.value = RenderState.lightDirection
		// Continuous seconds since first draw (no wrap at midnight) for the drift
		this.startJulian ??= state.realTime.julian
		this.time.value = (state.realTime.julian - this.startJulian) * 86400
	}
}
